import React, { useState } from "react";
import { Drawer, Form, Select, Input, InputNumber, Button, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { GET_VEHICLE_MAKES, GET_VEHICLE_MODELS } from "../../../graphql/query";

const { Option } = Select;
const { TextArea } = Input;

const WishlistDrawer = ({ visible, onClose, selectedLead, onSave }) => {
  const [form] = Form.useForm();
  const [search, setSearch] = useState({ make: "", model: "" });
  const [vehicleData, setVehicleData] = useState({
    makes: [],
    models: [],
  });

  const currentYear = dayjs().year();
  const years = Array.from({ length: 30 }, (_, index) => currentYear - index);

  // Watch form values for conditional queries
  const yearValue = Form.useWatch("year", form);
  const makeValue = Form.useWatch("make", form);

  const { loading: makeLoader } = useQuery(GET_VEHICLE_MAKES, {
    variables: {
      search: search.make,
    },
    skip: !visible,
    onCompleted: res => {
      setVehicleData(prev => ({ ...prev, makes: res.getVehicleMakes.data || [] }));
    },
  });

  const { loading: modelLoader } = useQuery(GET_VEHICLE_MODELS, {
    variables: {
      makeName: makeValue,
      year: yearValue,
      search: search.model,
    },
    skip: !visible || !makeValue || !yearValue,
    onCompleted: res => {
      setVehicleData(prev => ({ ...prev, models: res.getVehicleModels.data || [] }));
    },
  });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Create wishlist vehicle object
      const wishlistVehicle = {
        leadId: selectedLead?.id,
        condition: values.condition,
        year: values.year,
        make: values.make,
        model: values.model,
        trim: values.trim || null,
        vin: values.vin || null,
        exteriorColor: values.exteriorColor || null,
        interiorColor: values.interiorColor || null,
        bodyStyle: values.bodyStyle || null,
        price: values.price || null,
        mileage: values.mileage || null,
        comments: values.comments || null,
      };

      message.success("Vehicle added to wishlist");
      form.resetFields();

      // Call parent onSave if provided
      if (onSave) {
        onSave(wishlistVehicle);
      }

      onClose();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title={
        <div className="flex items-center justify-between">
          <span>Add to wishlist</span>
          <CloseOutlined className="cursor-pointer" onClick={handleCancel} />
        </div>
      }
      placement="right"
      open={visible}
      onClose={handleCancel}
      width={420}
      closeIcon={null}
      footer={
        <div className="flex justify-start gap-2">
          <Button type="primary" onClick={handleSave}>
            Add to wishlist
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        {selectedLead && (
          <Form.Item label="Select Lead">
            <Input value={selectedLead?.fullName} disabled />
          </Form.Item>
        )}

        <Form.Item
          name="condition"
          label="Condition:"
          rules={[{ required: true, message: "Please select condition" }]}
        >
          <Select placeholder="Used/New">
            <Option value="new">New</Option>
            <Option value="used">Used</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="year"
          label="Year:"
          rules={[{ required: true, message: "Please select year" }]}
        >
          <Select
            placeholder="Select"
            showSearch
            filterOption={(input, option) =>
              option.children.toString().toLowerCase().includes(input.toLowerCase())
            }
          >
            {years.map(year => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="make"
          label="Make:"
          rules={[{ required: true, message: "Please select make" }]}
        >
          <Select
            placeholder="Select"
            showSearch
            loading={makeLoader}
            onSearch={value => setSearch(prev => ({ ...prev, make: value }))}
            filterOption={false}
            disabled={!yearValue}
          >
            {vehicleData.makes.map(make => (
              <Option key={make.MakeId} value={make.MakeName}>
                {make.MakeName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="model"
          label="Model:"
          rules={[{ required: true, message: "Please select model" }]}
        >
          <Select
            placeholder="Select"
            showSearch
            loading={modelLoader}
            onSearch={value => setSearch(prev => ({ ...prev, model: value }))}
            filterOption={false}
            disabled={!makeValue || !yearValue}
          >
            {vehicleData.models.map(model => (
              <Option key={model.ModelId} value={model.ModelName}>
                {model.ModelName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="trim" label="Trim:">
          <Select placeholder="Select" allowClear>
            <Option value="">Select</Option>
          </Select>
        </Form.Item>

        <Form.Item name="vin" label="VIN:">
          <Input placeholder="Input" />
        </Form.Item>

        <Form.Item name="exteriorColor" label="Exterior Color:">
          <Input placeholder="Input" />
        </Form.Item>

        <Form.Item name="interiorColor" label="Interior Color:">
          <Input placeholder="Input" />
        </Form.Item>

        <Form.Item name="bodyStyle" label="Body Style:">
          <Select placeholder="Select" allowClear>
            <Option value="Sedan">Sedan</Option>
            <Option value="SUV">SUV</Option>
            <Option value="Truck">Truck</Option>
            <Option value="Coupe">Coupe</Option>
            <Option value="Convertible">Convertible</Option>
            <Option value="Wagon">Wagon</Option>
            <Option value="Van">Van</Option>
            <Option value="Hatchback">Hatchback</Option>
          </Select>
        </Form.Item>

        <Form.Item name="price" label="Price:">
          <InputNumber
            prefix="$"
            placeholder="Input"
            className="w-full"
            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={value => value.replace(/\$\s?|(,*)/g, "")}
          />
        </Form.Item>

        <Form.Item name="mileage" label="Mileage:">
          <InputNumber
            placeholder="Input"
            className="w-full"
            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={value => value.replace(/\$\s?|(,*)/g, "")}
          />
        </Form.Item>

        <Form.Item name="comments" label="Comments:">
          <TextArea rows={3} placeholder="Textarea" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default WishlistDrawer;
